import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bug, ImagePlus, Lightbulb, Loader2, Maximize2, Minus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/query-client";
import { cn } from "@/lib/utils";
import {
  FEEDBACK_API,
  FEEDBACK_LIMITS,
  FEEDBACK_MAX_SCREENSHOTS,
  FEEDBACK_SCREENSHOT_ACCEPT,
  FEEDBACK_SCREENSHOT_MAX_BYTES,
  FEEDBACK_SCREENSHOT_MIME_TYPES,
  redactPageUrl,
  type FeedbackItemDetail,
  type FeedbackType,
} from "@shared/feedbackBoard";
import { FEEDBACK_ITEMS_KEY, USER_ORGANIZATIONS_KEY } from "./use-feedback-board";
import {
  appendFeedbackScreenshots,
  removeFeedbackScreenshot,
  setFeedbackCollapsed,
  setFeedbackPosition,
  submitFeedback,
  updateFeedbackDraft,
  useFeedbackPanelState,
} from "./feedback-panel-store";

interface FeedbackSubmitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canSubmit: boolean;
}

export function FeedbackSubmitDialog({ open, onOpenChange, canSubmit }: FeedbackSubmitDialogProps) {
  // Mounted only while open. The draft (fields + screenshots), the collapsed state and the
  // position live in the shared store, not component state, so they survive both collapse/expand
  // and the cross-route remount that unmounts this panel and mounts a fresh one while it stays
  // open; they are cleared only when the panel actually closes. The page and viewport are captured
  // at submit time (the panel is non-modal, so the user can navigate or resize while it stays open).
  if (!open) return null;
  return <FeedbackFloatingPanel onOpenChange={onOpenChange} canSubmit={canSubmit} />;
}

/** Keep the panel this many px inside every viewport edge (matches the ~8px side gutter the width leaves). */
const PANEL_MARGIN = 8;
/** Space kept under the panel's default spot for the floating Bugs & Features button and Chat pill (bottom-[8.5rem]). */
const DEFAULT_BOTTOM_CLEARANCE = 136;
/** Arrow-key nudge while the drag handle is focused. */
const ARROW_STEP = 16;

interface PanelPosition {
  left: number;
  top: number;
}

function FeedbackFloatingPanel({
  onOpenChange,
  canSubmit,
}: {
  onOpenChange: (open: boolean) => void;
  canSubmit: boolean;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const collapseButtonRef = useRef<HTMLButtonElement>(null);
  const dragRef = useRef<{ pointerId: number; offsetX: number; offsetY: number } | null>(null);

  // Collapsed state and position live in the store so they survive a cross-route remount.
  const { collapsed, position } = useFeedbackPanelState();
  const setCollapsed = setFeedbackCollapsed;
  const setPosition = setFeedbackPosition;

  // Keep the panel fully inside the viewport (clamped to PANEL_MARGIN; on a viewport too small
  // for the panel the max collapses back to the margin, so it still fits).
  const clampToViewport = useCallback((left: number, top: number): PanelPosition => {
    const el = panelRef.current;
    const width = el?.offsetWidth ?? 0;
    const height = el?.offsetHeight ?? 0;
    const maxLeft = Math.max(PANEL_MARGIN, window.innerWidth - width - PANEL_MARGIN);
    const maxTop = Math.max(PANEL_MARGIN, window.innerHeight - height - PANEL_MARGIN);
    return {
      left: Math.min(Math.max(left, PANEL_MARGIN), maxLeft),
      top: Math.min(Math.max(top, PANEL_MARGIN), maxTop),
    };
  }, []);

  const focusTitle = useCallback(() => {
    panelRef.current?.querySelector<HTMLElement>("#feedback-title")?.focus();
  }, []);

  // A position already in the store at mount means a previous panel instance measured it: this is a
  // cross-route remount, not a fresh open. Captured once (useRef init) before the effects below set it.
  const isRemount = useRef(position != null).current;

  // Fresh open: park at the right edge, bottom above the floating button and Chat pill (clamped into view).
  // Remount: keep the stored position (the collapse effect below re-clamps it to the current viewport).
  useLayoutEffect(() => {
    if (isRemount) return;
    const height = panelRef.current?.offsetHeight ?? 0;
    setPosition(clampToViewport(Number.POSITIVE_INFINITY, window.innerHeight - height - DEFAULT_BOTTOM_CLEARANCE));
  }, [isRemount, clampToViewport]);

  // On a fresh open, focus the first field and return focus to the opener on close. On a remount we
  // leave focus alone — the user was interacting elsewhere, so stealing it back would be jarring.
  useEffect(() => {
    if (isRemount) return;
    const opener = document.activeElement as HTMLElement | null;
    focusTitle();
    return () => {
      if (opener && document.contains(opener)) opener.focus?.();
    };
  }, [isRemount, focusTitle]);

  // Re-clamp when the viewport shrinks so the panel never ends up off-screen.
  useEffect(() => {
    const onResize = () => setPosition((prev) => (prev ? clampToViewport(prev.left, prev.top) : prev));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clampToViewport]);

  // Expanding near the bottom edge would push the taller panel off-screen; re-clamp on every size change.
  useLayoutEffect(() => {
    setPosition((prev) => (prev ? clampToViewport(prev.left, prev.top) : prev));
  }, [collapsed, clampToViewport]);

  // Move focus with the collapse state: to the Expand button on collapse, back to the title on expand.
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    if (collapsed) collapseButtonRef.current?.focus();
    else focusTitle();
  }, [collapsed, focusTitle]);

  function onHeaderPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    // Let the collapse/close buttons work normally; only bare-header drags move the panel.
    if ((event.target as HTMLElement).closest("button")) return;
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragRef.current = { pointerId: event.pointerId, offsetX: event.clientX - rect.left, offsetY: event.clientY - rect.top };
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // jsdom and some browsers can reject capture; dragging still works without it.
    }
  }

  function onHeaderPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    // A mouse released where we never saw the pointerup (capture refused): stop dragging, don't follow hover.
    if (event.pointerType === "mouse" && event.buttons === 0) {
      dragRef.current = null;
      return;
    }
    setPosition(clampToViewport(event.clientX - drag.offsetX, event.clientY - drag.offsetY));
  }

  function onHeaderPointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    if (!dragRef.current) return;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // Capture may never have been granted; ignore.
    }
    dragRef.current = null;
  }

  function onHeaderKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    const nudges: Record<string, [number, number]> = {
      ArrowLeft: [-ARROW_STEP, 0],
      ArrowRight: [ARROW_STEP, 0],
      ArrowUp: [0, -ARROW_STEP],
      ArrowDown: [0, ARROW_STEP],
    };
    const nudge = nudges[event.key];
    if (!nudge) return;
    event.preventDefault();
    setPosition((prev) => clampToViewport((prev?.left ?? 0) + nudge[0], (prev?.top ?? 0) + nudge[1]));
  }

  // This handler sits on the panel root, so it only fires for Esc pressed while focus is inside the
  // panel — Esc elsewhere on the page is left alone. Expanded: collapse; already collapsed: close.
  function onPanelKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    if (collapsed) onOpenChange(false);
    else setCollapsed(true);
  }

  const panel = (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onKeyDown={onPanelKeyDown}
      className="fixed z-50 flex flex-col overflow-hidden rounded-lg border bg-card text-card-foreground shadow-xl"
      style={{
        left: position?.left,
        top: position?.top,
        width: "min(28rem, 100vw - 16px)",
        maxHeight: "min(calc(100dvh - 16px), 40rem)",
        visibility: position ? undefined : "hidden",
      }}
      data-testid="feedback-submit-dialog"
    >
      <div
        tabIndex={0}
        aria-label="Bugs & Features — drag or use the arrow keys to move"
        onPointerDown={onHeaderPointerDown}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={onHeaderPointerUp}
        onPointerCancel={onHeaderPointerUp}
        onKeyDown={onHeaderKeyDown}
        className="flex shrink-0 cursor-move touch-none select-none items-center gap-2 border-b px-4 py-3 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
        data-testid="feedback-panel-header"
      >
        <h2 id={titleId} className="min-w-0 flex-1 truncate text-sm font-semibold leading-none">
          Bugs & Features
        </h2>
        <Button
          ref={collapseButtonRef}
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          aria-label={collapsed ? "Expand" : "Collapse"}
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((value) => !value)}
          data-testid="feedback-panel-collapse"
        >
          {collapsed ? <Maximize2 className="h-4 w-4" aria-hidden="true" /> : <Minus className="h-4 w-4" aria-hidden="true" />}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          aria-label="Close"
          onClick={() => onOpenChange(false)}
          data-testid="feedback-panel-close"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
      <div hidden={collapsed} className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4" data-testid="feedback-panel-body">
        <p id={descriptionId} className="text-sm text-muted-foreground">
          Report a bug or request a feature. It goes to the ClaimHive team.
        </p>
        <FeedbackSubmitForm canSubmit={canSubmit} onDone={() => onOpenChange(false)} />
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}

/** Redacted current page, from the live URL (not wouter, which drops the query string). */
function currentPagePath(): string {
  return redactPageUrl(window.location.pathname + window.location.search);
}

const MAX_MB = Math.round(FEEDBACK_SCREENSHOT_MAX_BYTES / (1024 * 1024));

function isAllowedImage(file: File): boolean {
  return (FEEDBACK_SCREENSHOT_MIME_TYPES as readonly string[]).includes(file.type);
}

function FeedbackSubmitForm({ canSubmit, onDone }: { canSubmit: boolean; onDone: () => void }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Subscribe to navigation so the "Page" row re-renders as the user moves around with the
  // panel open; the displayed value itself comes from the live URL (including the query string).
  useLocation();
  const userAgent = navigator.userAgent;

  // The draft (typed fields + screenshots) lives in the store so it survives a cross-route remount;
  // fileError and isDragging are transient UI and stay local. `submitting`/`submitError` also live in
  // the store so the submit completes (and Send stays disabled) even if this form unmounts mid-flight.
  const panelState = useFeedbackPanelState();
  const { type, title, steps, expectedVsActual, description, screenshots } = panelState.draft;
  const { submitting, submitError } = panelState;
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: orgs = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: USER_ORGANIZATIONS_KEY,
    enabled: !!user,
  });
  const firmName = user?.activeOrgId ? orgs.find((org) => org.id === user.activeOrgId)?.name : undefined;
  const userName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const userLabel = userName && user?.email ? `${userName} (${user.email})` : userName || user?.email || "Unknown";

  function addFiles(list: FileList | File[] | null) {
    const files = Array.from(list ?? []);
    if (files.length === 0) return;
    const problems: string[] = [];
    const accepted: File[] = [];
    let room = FEEDBACK_MAX_SCREENSHOTS - screenshots.length;
    for (const file of files) {
      if (!isAllowedImage(file)) {
        problems.push(`${file.name} is not a PNG, JPG or WebP image.`);
      } else if (file.size > FEEDBACK_SCREENSHOT_MAX_BYTES) {
        problems.push(`${file.name} is larger than ${MAX_MB} MB.`);
      } else if (room <= 0) {
        problems.push(`You can add up to ${FEEDBACK_MAX_SCREENSHOTS} screenshots.`);
        break;
      } else {
        accepted.push(file);
        room -= 1;
      }
    }
    // The store owns the preview URLs and keys so they outlive this component across a remount.
    appendFeedbackScreenshots(accepted);
    setFileError(problems.length ? problems.join(" ") : null);
  }

  function removeScreenshot(key: number) {
    removeFeedbackScreenshot(key);
    setFileError(null);
  }

  function handleDragLeave(event: DragEvent<HTMLDivElement>) {
    // Moving over the zone's own children also fires dragleave.
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsDragging(false);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragging(false);
    addFiles(event.dataTransfer.files);
  }

  function startSubmit() {
    // Runs through the store so completion (clear draft + close, toast, invalidate) survives this
    // form unmounting mid-flight. The request builder captures the draft as it is at click time;
    // the success side effects below use stable references, so they are safe to call after unmount.
    const request = async () => {
      // The server takes the user, the firm and the browser from the session and request.
      const form = new FormData();
      form.append("type", type);
      form.append("title", title.trim());
      if (type === "bug") {
        form.append("stepsToReproduce", steps.trim());
        if (expectedVsActual.trim()) form.append("expectedVsActual", expectedVsActual.trim());
        // Captured now, not when the panel opened: it is non-modal, so the user may have
        // navigated or resized since.
        form.append("pagePath", currentPagePath());
        form.append("viewportWidth", String(window.innerWidth));
        form.append("viewportHeight", String(window.innerHeight));
        for (const shot of screenshots) form.append("screenshots", shot.file, shot.file.name);
      } else {
        form.append("description", description.trim());
      }
      const res = await apiRequest("POST", `${FEEDBACK_API}/items`, form);
      return (await res.json()) as FeedbackItemDetail;
    };
    void submitFeedback(
      request,
      // onSent: the report was delivered — always toast and refresh, even if the panel has since
      // closed and a new draft started.
      () => {
        toast({ title: "Sent to ClaimHive", description: "Thanks. You can follow it on the Bugs & Features page." });
        queryClient.invalidateQueries({ queryKey: FEEDBACK_ITEMS_KEY });
      },
      // onDone: close the panel, but only when this submit is still the current one.
      onDone,
    );
  }

  const requiredFilled = !!title.trim() && (type === "bug" ? !!steps.trim() : !!description.trim());
  const submitDisabled = !canSubmit || submitting || !requiredFilled;
  const isFull = screenshots.length >= FEEDBACK_MAX_SCREENSHOTS;

  return (
    <form
      className="min-w-0 space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!submitDisabled) startSubmit();
      }}
    >
      <ToggleGroup
        type="single"
        variant="outline"
        value={type}
        // Radix lets a single toggle group be emptied; keep one choice selected.
        onValueChange={(value) => value && updateFeedbackDraft({ type: value as FeedbackType })}
        className="grid grid-cols-2 gap-2"
        aria-label="What are you sending?"
      >
        <ToggleGroupItem value="bug" className="w-full" data-testid="feedback-type-bug">
          <Bug aria-hidden="true" />
          Bug
        </ToggleGroupItem>
        <ToggleGroupItem value="feature" className="w-full" data-testid="feedback-type-feature">
          <Lightbulb aria-hidden="true" />
          Feature request
        </ToggleGroupItem>
      </ToggleGroup>

      <div className="space-y-1.5">
        <Label htmlFor="feedback-title">Title</Label>
        <Input
          id="feedback-title"
          value={title}
          onChange={(e) => updateFeedbackDraft({ title: e.target.value })}
          maxLength={FEEDBACK_LIMITS.title}
          required
          placeholder={type === "bug" ? "What went wrong?" : "What would you like ClaimHive to do?"}
          data-testid="feedback-title"
        />
      </div>

      {type === "bug" ? (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="feedback-steps">Steps to reproduce</Label>
            <Textarea
              id="feedback-steps"
              value={steps}
              onChange={(e) => updateFeedbackDraft({ steps: e.target.value })}
              maxLength={FEEDBACK_LIMITS.longText}
              required
              rows={4}
              placeholder={"1. Open a claim\n2. Click …\n3. …"}
              data-testid="feedback-steps"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="feedback-expected">
              Expected vs actual <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="feedback-expected"
              value={expectedVsActual}
              onChange={(e) => updateFeedbackDraft({ expectedVsActual: e.target.value })}
              maxLength={FEEDBACK_LIMITS.longText}
              rows={3}
              placeholder="What you expected, and what happened instead"
              data-testid="feedback-expected"
            />
          </div>

          <div className="space-y-1.5">
            <p className="text-sm font-medium leading-none">
              Screenshots <span className="font-normal text-muted-foreground">(optional, up to {FEEDBACK_MAX_SCREENSHOTS})</span>
            </p>
            <div
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              data-testid="feedback-screenshot-dropzone-area"
            >
              {/* A button inside the drop area, so the picker opens from the keyboard too. */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isFull}
                aria-describedby="feedback-screenshot-help"
                className={cn(
                  "flex w-full flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-border px-4 py-4 text-center text-sm text-muted-foreground transition-colors",
                  "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
                  "disabled:cursor-not-allowed disabled:opacity-60",
                  isDragging && "border-primary bg-primary/5",
                )}
                data-testid="feedback-screenshot-dropzone"
              >
                <ImagePlus className="h-5 w-5" aria-hidden="true" />
                <span>
                  {isFull ? (
                    "Screenshot limit reached"
                  ) : (
                    <>
                      <span className="font-medium text-foreground">Choose images</span> or drag them here
                    </>
                  )}
                </span>
                <span id="feedback-screenshot-help" className="text-xs">
                  PNG, JPG or WebP · {MAX_MB} MB each
                </span>
              </button>
              <input
                ref={fileInputRef}
                id="feedback-screenshot-input"
                type="file"
                accept={FEEDBACK_SCREENSHOT_ACCEPT}
                multiple
                className="hidden"
                tabIndex={-1}
                aria-label="Screenshots"
                onChange={(event) => {
                  addFiles(event.target.files);
                  // Lets the same file be picked again after a remove.
                  event.target.value = "";
                }}
                data-testid="feedback-screenshot-input"
              />
            </div>
            {fileError && (
              <p className="text-sm text-destructive" role="alert" data-testid="feedback-screenshot-error">
                {fileError}
              </p>
            )}
            {screenshots.length > 0 && (
              <ul className="grid grid-cols-3 gap-2" data-testid="feedback-screenshot-list">
                {screenshots.map((shot) => (
                  <li key={shot.key} className="relative min-w-0">
                    <img
                      src={shot.previewUrl}
                      alt={`Screenshot ${shot.file.name}`}
                      className="h-20 w-full rounded-md border border-border object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeScreenshot(shot.key)}
                      className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-background/90 text-foreground shadow ring-offset-background hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={`Remove ${shot.file.name}`}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                    <p className="mt-1 truncate text-xs text-muted-foreground">{shot.file.name}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div
            className="space-y-1.5 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground"
            data-testid="feedback-captured-context"
          >
            <p className="font-medium text-foreground">Attached to your report</p>
            <dl className="space-y-1">
              <ContextRow label="Page" value={currentPagePath() || "/"} testId="feedback-context-page" />
              <ContextRow label="Browser" value={userAgent} testId="feedback-context-browser" />
              <ContextRow
                label="Viewport"
                value={`${window.innerWidth}x${window.innerHeight}`}
                testId="feedback-context-viewport"
              />
              <ContextRow label="User" value={userLabel} testId="feedback-context-user" />
              <ContextRow
                label="Firm"
                value={user?.activeOrgId ? firmName ?? "Your current firm" : "No firm"}
                testId="feedback-context-firm"
              />
            </dl>
            <p>The user and firm come from your session.</p>
          </div>
        </>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor="feedback-description">Why it matters</Label>
          <Textarea
            id="feedback-description"
            value={description}
            onChange={(e) => updateFeedbackDraft({ description: e.target.value })}
            maxLength={FEEDBACK_LIMITS.longText}
            required
            rows={5}
            placeholder="What would it help you do, and how often?"
            data-testid="feedback-description"
          />
        </div>
      )}

      {!canSubmit && (
        <p className="text-sm text-muted-foreground" data-testid="feedback-no-firm">
          You have no firm, so you can't submit items.
        </p>
      )}
      {submitError && (
        <p className="text-sm text-destructive" role="alert" data-testid="feedback-submit-error">
          {submitError}
        </p>
      )}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Link
          href="/feedback"
          onClick={onDone}
          className="text-center text-sm text-primary underline-offset-4 hover:underline sm:text-left"
        >
          See your firm's items and the roadmap
        </Link>
        <Button type="submit" disabled={submitDisabled} data-testid="feedback-submit">
          {submitting && <Loader2 className="animate-spin" aria-hidden="true" />}
          Send to ClaimHive
        </Button>
      </div>
    </form>
  );
}

function ContextRow({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div className="flex min-w-0 gap-2">
      <dt className="w-16 shrink-0 font-medium">{label}</dt>
      <dd className="min-w-0 break-all text-foreground" data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}
